import React, { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Check, ChevronRight } from 'lucide-react';
import { WizardStep } from '../../types';

interface WizardProps {
  steps: WizardStep[];
  onComplete: (data: any) => void;
  onCancel: () => void;
  initialData?: any;
  initialStep?: number;
  suppressLeaveWarning?: boolean;
  isWorkOrderCreated?: boolean; // New prop to indicate if work order has been created
  onWorkOrderCreated?: () => void; // Callback when work order is created
}

const Wizard: React.FC<WizardProps> = ({
  steps,
  onComplete,
  onCancel,
  initialData = {},
  initialStep = 0,
  suppressLeaveWarning = false,
  isWorkOrderCreated = false,
  onWorkOrderCreated,
}) => {
  const [currentStep, setCurrentStep] = useState(() =>
    Math.min(Math.max(initialStep, 0), Math.max(steps.length - 1, 0))
  );
  const [formData, setFormData] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const step1Changed = !!(
    (formData && (
      formData.selectedClient ||
      formData.selectedClientLocationId ||
      formData.zipcode ||
      formData.radius ||
      (Array.isArray(formData.selectedCategories) && formData.selectedCategories.length > 0)
    ))
  );
  const hasUnsavedChanges = !isWorkOrderCreated
    && !suppressLeaveWarning
    && (currentStep > 0 || step1Changed);
  const lastLeaveUrlRef = useRef<string | null>(null);
  const lastPromptAtRef = useRef<number>(0);
  const suppressBeforeUnloadRef = useRef<boolean>(false);

  const currentStepData = steps[currentStep];
  const CurrentStepComponent = currentStepData.component;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  // Scroll to top when step changes
  useEffect(() => {
    // Defer to next tick to allow layout after step change
    const id = window.setTimeout(() => {
      if (topRef.current) {
        try { topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
      }
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
      try { document.documentElement.scrollTop = 0; document.body.scrollTop = 0; } catch {}
    }, 0);
    return () => window.clearTimeout(id);
  }, [currentStep]);

  // Warn on browser/tab close or navigation while in-progress
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (suppressBeforeUnloadRef.current) return; // allow clean navigation without alert
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Intercept in-app link clicks (including Link from react-router which renders <a>)
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const anchor = target ? target.closest('a') as HTMLAnchorElement | null : null;
      if (!anchor) return;
      if (anchor.target === '_blank') return;
      if (!anchor.href) return;
      // Only same-origin navigations
      try {
        const url = new URL(anchor.href);
        if (url.origin !== window.location.origin) return;
        // Allow staying within the create wizard route
        const leavingWizard = !url.pathname.startsWith('/workorders/create');
        if (leavingWizard) {
          e.preventDefault();
          e.stopPropagation();

          const now = Date.now();
          const sameUrl = lastLeaveUrlRef.current === url.pathname + url.search + url.hash;
          const withinWindow = now - lastPromptAtRef.current < 5000;

          if (sameUrl && withinWindow) {
            // proceed with navigation
            lastLeaveUrlRef.current = null;
            lastPromptAtRef.current = 0;
            suppressBeforeUnloadRef.current = true;
            window.location.assign(url.href);
            return;
          }

          lastLeaveUrlRef.current = url.pathname + url.search + url.hash;
          lastPromptAtRef.current = now;
          toast((t) => (
            <span className="text-sm">
              You have unsaved changes. Click the link again to leave.
            </span>
          ), { id: 'wizard-unsaved-warning', duration: 4000, icon: '⚠️' });
        }
      } catch {}
    };
    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [hasUnsavedChanges]);

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepDataChange = useCallback((data: any) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await onComplete(formData);
    } catch (error) {
      console.error('Error completing wizard:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'upcoming';
  };

  return (
    <div ref={topRef} className="max-w-4xl mx-auto">
      {/* Progress indicator */}
      <nav aria-label="Progress" className="mb-8">
        <ol className="flex items-center">
          {steps.map((step, stepIdx) => {
            const status = getStepStatus(stepIdx);
            
            return (
              <li key={step.id} className={`relative ${stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''} flex-1`}>
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className={`h-0.5 w-full ${
                    status === 'completed' ? 'bg-accent-color' : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                </div>
                <div className={`relative flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                  status === 'completed'
                    ? 'bg-accent-color border-accent-color'
                    : status === 'current'
                    ? 'bg-white dark:bg-gray-800 border-accent-color'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                }`}>
                  {status === 'completed' ? (
                    <Check className="h-5 w-5 text-white" />
                  ) : status === 'current' ? (
                    <span className="h-2.5 w-2.5 rounded-full bg-accent-color" />
                  ) : (
                    <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  )}
                  <span className="sr-only">{step.title}</span>
                </div>
                <div className="absolute top-10 left-1/2 transform -translate-x-1/2">
                  <span className={`text-xs font-medium ${
                    status === 'completed' || status === 'current'
                      ? 'text-accent-color'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Step content */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        {/* Step header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Step {currentStep + 1} of {steps.length}: {currentStepData.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {currentStepData.description}
              </p>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {currentStep + 1} / {steps.length}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-6">
          <CurrentStepComponent
            data={formData}
            onChange={handleStepDataChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            isSubmitting={isSubmitting}
            onWorkOrderCreated={onWorkOrderCreated}
          />
        </div>

        {/* Navigation is handled by each individual step component */}
      </div>
    </div>
  );
};

export default Wizard;
